import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as classNames from 'classnames';
import ReviewRouterService from '../../services/ReviewRouterService/ReviewRouterService';
import MarkerModel from '../../types/MarkerModel';
import IVideoPlayer from '../../types/IVideoPlayer';
import ReviewVersionModel from '../../types/ReviewVersionModel';
import TimeFormatter from '../../utils/TimeFormatter/TimeFormatter';
import Strings from '../../globals/Strings';
import CommentMarker from '../../assets/CommentMarker.svg';
import {RouteComponentProps} from 'react-router';

const commentPreviewWidth = 200;
const previewHalfWidth = commentPreviewWidth / 2;
const markerWidth = 14;
const halfMarkerWidth = markerWidth / 2;
const edgeOffset = 10;

const calculatePreviewX = (target: any) => {
   const targetRect = target.getBoundingClientRect();
   const playerRect = document.querySelector('.tsc-smartplayer').getBoundingClientRect();
   const offsetX = targetRect.left + halfMarkerWidth - playerRect.left;
   const centeredOffsetX = offsetX - previewHalfWidth > 0 ? offsetX - previewHalfWidth : -edgeOffset;
   const maxX = playerRect.width - (commentPreviewWidth - edgeOffset);

   return centeredOffsetX < maxX ? centeredOffsetX : maxX;
};

export default class CommentMarkers extends React.Component<CommentMarkersProps, CommentMarkersState> {
   readonly state: CommentMarkersState = {showPreview: false, previewX: 0, previewAuthorName: '', previewCommentText: ''};

   constructor(props: CommentMarkersProps) {
      super(props);
      autoBind(this);
   }

   getPercentFromTime(time: number): number {
      return Math.max(Math.min(time / this.props.playDuration * 100, 100), 0);
   }

   onMarkerClick(e: any, marker: MarkerModel) {
      const {history, reviewId, player, setSelectedComment, reviewVersion} = this.props;
      setSelectedComment(marker.commentId);
      player.pause();
      player.setCurrentTime(marker.time);
      ReviewRouterService.navigateToReviewComment(history, reviewId, reviewVersion.VersionNumber, marker.commentId);
      ReviewRouterService.navigateToReviewOnTimeMismatch(history, player, reviewId, reviewVersion.VersionNumber, marker.commentId, marker.time);
   }

   showPreview(target: any, marker: MarkerModel) {
      const previewX = calculatePreviewX(target);
      const previewText = !marker.commentText && marker.hasAnnotation ? Strings.defaults.annotationAttached : marker.commentText;
      this.setState({
         previewAuthorName: marker.authorName,
         previewCommentText: previewText,
         showPreview: true,
         previewX: previewX
      });
   }

   hidePreview() {
      this.setState({
         previewAuthorName: '',
         previewCommentText: '',
         showPreview: false
      });
   }

   render() {
      const {markers, selectedCommentTime} = this.props;
      const {showPreview, previewAuthorName, previewCommentText, previewX} = this.state;
      const previewElement = showPreview && (
         <div className="comment-preview" style={{left: `${previewX}px`}}>
            <div className="comment-preview-author">
               {previewAuthorName}
            </div>
            <div className="comment-preview-body">
               {previewCommentText}
            </div>
         </div>
      );

      return (
         <React.Fragment>
            {markers.map(marker => {
               const markerClassNames = classNames('comment-marker', {selected: marker.time === selectedCommentTime});
               const markerPercentPosition = this.getPercentFromTime(marker.time);
               const ariaLabel = Strings.accessibility.comments.commentMarkAriaLabel
                  .replace('{time}', TimeFormatter.formatTimeForSpeech(marker.time))
                  .replace('{user}', previewAuthorName)
                  .replace('{commentText}', previewCommentText);

               return (
                  <button
                     key={`marker-${marker.time}`}
                     className={markerClassNames}
                     style={{left: `${markerPercentPosition}%`}}
                     onClick={(e: any) => this.onMarkerClick(e, marker)}
                     onMouseOver={(e: any) => this.showPreview(e.target, marker)}
                     onMouseLeave={this.hidePreview}
                     onFocus={(e: any) => this.showPreview(e.target, marker)}
                     onBlur={this.hidePreview}
                     aria-label={ariaLabel}
                     onTouchStart={(e: any) => this.onMarkerClick(e, marker)}>
                        <CommentMarker />
                  </button>
               );
            })}
            {previewElement}
         </React.Fragment>
      );
   }
}

export interface CommentMarkersProps extends RouteComponentProps {
   markers: MarkerModel[];
   playDuration: number;
   selectedCommentTime: number;
   player: IVideoPlayer;
   reviewId: string;
   reviewVersion: ReviewVersionModel;
   setSelectedComment: (id: string) => void;
}

export interface CommentMarkersState {
   showPreview: boolean;
   previewX: number;
   previewAuthorName: string;
   previewCommentText: string;
}
